import { PAGE_URL } from 'client/site-modules/shared/constants/page-urls';
import { INVENTORY_TYPES } from 'client/constants/inventory-types';

const BASE_CALCULATORS_BREADCRUMBS = [
  { title: 'Home', href: PAGE_URL.HOME },
  { title: 'Calculators', href: PAGE_URL.CALCULATORS },
];
export const AUTO_CALCULATORS_TITLE = 'Auto Calculators';
export const AUTO_CALCULATORS_SUBTITLE = `Ready to buy or lease your next car? Our free tools cover every financial step of the process. Use our calculators to get instant answers to your most important questions and make an informed decision:`;
export const AUTO_CALCULATORS_SUBTITLE_LIST = [
  'Calculate monthly loan or lease payments',
  'See whether leasing or buying is a better option for you',
  'Find out what you can really afford',
  'Learn the true financial benefit of switching to a fuel-efficient car',
];
export const CALCULATORS_BREADCRUMBS = [{ title: 'Home', href: PAGE_URL.HOME }, { title: AUTO_CALCULATORS_TITLE }];
export const AFFORDABILITY_BREADCRUMBS = [...BASE_CALCULATORS_BREADCRUMBS, { title: 'Affordability Calculator' }];
export const LOAN_PAYMENT_BREADCRUMBS = [
  ...BASE_CALCULATORS_BREADCRUMBS,
  { title: 'Auto Loan Monthly Payment Calculator' },
];
export const INCENTIVES_REBATES_BREADCRUMBS = [
  ...BASE_CALCULATORS_BREADCRUMBS,
  { title: 'Low APR vs. Cash Back Calculator' },
];
export const GAS_GUZZLER_BREADCRUMBS = [...BASE_CALCULATORS_BREADCRUMBS, { title: 'Gas Guzzler Calculator' }];
export const LEASE_BREADCRUMBS = [...BASE_CALCULATORS_BREADCRUMBS, { title: 'Car Lease Calculator' }];
export const LEASE_VS_BUY_BREADCRUMBS = [...BASE_CALCULATORS_BREADCRUMBS, { title: 'Lease vs Buy Car Calculator' }];

export const CALCULATORS_SECTIONS_MAP = [
  {
    url: '/calculators/simplified-pricing.html',
    title: 'Monthly Loan Payment',
    subtitle: 'Use this calculator to estimate your payment for a car loan.',
    button: {
      text: 'Calculate a loan payment',
      trackingId: 'open_loan_calculator',
    },
  },
  {
    url: '/calculators/car-lease.html',
    title: 'Monthly Lease Payment',
    subtitle: 'Use this calculator to estimate your payment for a car lease.',
    button: {
      text: 'How much is a lease payment?',
      trackingId: 'open_lease_calculator',
    },
  },
  {
    url: '/calculators/lease-vs-buy-calculator/',
    title: 'Lease vs Buy',
    subtitle: 'Compare monthly lease payment with a loan payment to find out if you should lease or buy your next car.',
    button: {
      text: 'Lease or Buy',
      trackingId: 'open_lease_vs_buy_calculator',
    },
  },
  {
    url: '/calculators/affordability.html',
    title: 'What can I afford?',
    subtitle: 'Use this calculator to find the price range for a new car.',
    button: {
      text: 'What is my budget?',
      trackingId: 'open_affordability_calculator',
    },
  },
  {
    url: '/calculators/incentives-rebates.html',
    title: 'Low APR vs. Cash Back Calculator',
    subtitle: 'Should you take the Low A.P.R. or the Cash Back? Find out.',
    button: {
      text: 'Help me choose an incentive',
      trackingId: 'open_incentive_calculator',
    },
  },
  {
    url: '/calculators/gas-guzzler.html',
    title: 'Gas Guzzler Trade-In Calculator',
    subtitle: 'Find out how much money you will save if you trade in your gas guzzler for a fuel-efficient car.',
    button: {
      text: 'Calculate gas mileage savings',
      trackingId: 'open_gas_guzzler_calculator',
    },
  },
  {
    url: '/partner-insurance/?subID2=calculatorLP',
    title: 'Compare offers and save on the cost of insurance',
    subtitle: 'Explore insurance offers that may help you save money.',
    button: {
      text: 'Get your offers',
      trackingId: 'open_loan_calculator',
    },
    isLast: true,
  },
];

export const PAYMENT_INPUT_PROPS = {
  leftColSizes: { xs: 12, md: 6 },
  rightColSizes: { xs: 12 },
  leftColClasses: 'text-gray-darker mb-0_5 mb-md-0 align-self-center',
  rightColClasses: 'me-auto text-end align-self-center',
  tooltipClassName: 'text-gray',
  tooltipIconClassName: 'px-0_5 pt-0_5 pb-0 p-md-0',
  inputClassName: 'text-gray-dark',
  className: 'mb-0',
};

export const TERM_NAME = 'term';
export const LEASE_TERM_NAME = 'leaseTerm';
export const INTEREST_RATE_NAME = 'interestRate';
export const CREDIT_SCORE_NAME = 'creditScore';
export const DOWN_PAYMENT_NAME = 'downPayment';
export const CASH_INCENTIVES_NAME = 'cashIncentives';
export const SALES_PRICE_NAME = 'salesPrice';

export const TERM_OPTIONS = [
  { value: '36', label: '36 months' },
  { value: '48', label: '48 months' },
  { value: '60', label: '60 months' },
  { value: '72', label: '72 months' },
];

export const LOAN_TERM_OPTIONS = [
  { value: '24', label: '24 months' },
  { value: '36', label: '36 months' },
  { value: '39', label: '39 months' },
  { value: '42', label: '42 months' },
  { value: '48', label: '48 months' },
  { value: '60', label: '60 months' },
  { value: '66', label: '66 months' },
  { value: '72', label: '72 months' },
  { value: '84', label: '84 months' },
];

export const TOOLTIPS = {
  TRADE_IN: {
    text: `The value of your currently owned vehicle credited towards the purchase or lease of the vehicle you
      are acquiring. If you select a vehicle using the "Value your trade-in" button, the value displayed in the
      calculator will be the Edmunds.com True Market Value trade-in price for a typically-equipped vehicle,
      assuming accumulated mileage of 15,000 miles per year.`,
    placement: 'bottom',
  },
  TRADE_IN_AFFORDABILITY: {
    text: `The value of your currently owned vehicle credited towards the purchase or lease of the vehicle you are purchasing.`,
    placement: 'bottom',
  },
  SALES_TAX: {
    text: 'Estimated sales tax rate for the selected zip code applied to the sales price.',
    placement: 'bottom',
  },
  MSRP: {
    text: "Update this price to the Manufacturer's Suggested Retail Price for the vehicle you are buying including any installed optional equipment, but excluding taxes and fees. We'll determine the suggested price to pay based on the Style you selected.",
    placement: 'bottom',
  },
  SALES_PRICE: {
    text: 'The selling price of the new or used vehicle for monthly loan payment calculation.',
    placement: 'bottom',
  },
  MONTHLY_PAYMENT: {
    text: 'This is your desired monthly payment on your car loan.',
    placement: 'bottom',
  },
  REGISTRATION_FEES: {
    text: 'Add title and registration here to include them in your estimated monthly payment.',
    placement: 'bottom',
  },
  CASH_INCENTIVES: {
    text: 'Available incentives and rebates included in the monthly payment estimate.',
    placement: 'bottom',
  },
  INTEREST_RATE: {
    text: 'Generally available financing interest rate for the estimated loan payment.',
    placement: 'bottom',
  },
  APR: {
    text: 'Available promotional interest rate replacing the market interest rate.',
    placement: 'bottom',
  },
  AMOUNT_OWED_TRADE_IN: {
    text: `The remaining balance on a loan for your trade-in will be deducted from the trade-in value.`,
    placement: 'bottom',
  },
  OWED_TRADE_IN: {
    text: `The amount still owed on the current financing, if any, on your trade-in. When you trade your vehicle
      into a dealer, the dealer will normally pay off the loan balance to secure clear title. The difference between
      the trade-in value and the actual payoff on the trade is treated as a credit/debit against the Vehicle Sale
      Price.`,
    placement: 'bottom',
  },
  CASH_DOWN_PAYMENT: {
    text: `The cash down payment will reduce the financed loan amount.`,
    placement: 'bottom',
  },
  DOWN_PAYMENT: {
    text: `For a vehicle purchase, the amount of your down payment. For a vehicle lease, an initial payment that
      reduces the capitalized cost (i.e., the amount you finance) or that is applied to other amounts payable at
      lease signing.`,
    placement: 'bottom',
  },
  MARKET_RATE: {
    text: 'For a vehicle purchase, the cost of credit (i.e., the interest rate) expressed as an annual rate.',
    placement: 'bottom',
  },
  MONEY_FACTOR: {
    text: `In leasing, the money factor is essentially the interest rate you'll pay during your lease. It's sometimes called a "lease factor" or even a "lease fee". We typically show the "buy rate", which is the rate set by the finance company. However, each dealer may include additional markup on this rate, resulting in a higher money factor than what is shown here. This rate may only apply to certain buyers. See dealer for details.`,
    placement: 'bottom',
  },
  ACQUISITION_FEE: {
    text: `Sometimes called a bank fee or administrative fee, this is a fee that leasing companies charge to arrange the lease.`,
    placement: 'bottom',
  },
  RESIDUAL_VALUES: {
    text: `Remaining value of the vehicle at the end of the lease term. This may range from 54% - 68%.`,
    placement: 'bottom',
  },
  ANNUAL_MILES: {
    text: `Expected miles driven each year. This will affect both residual value and finance terms.`,
    placement: 'bottom',
  },
  CAP_COST_REDUCTION: {
    text: 'The cap cost reduction is cash paid up front to reduce the monthly lease payment.',
    placement: 'bottom',
  },
};

export const LOAN_EDITORIAL_CONTENT = [
  {
    title: 'Use the Edmunds Auto Loan Calculator to Determine or Verify Your Payment',
    items: [
      `You're gearing up to buy your next car but aren't sure what the monthly car payment will look like. Getting to a monthly payment usually involves some math, but the good news is that the Edmunds auto loan calculator will do the heavy lifting for you.`,
      `Let's say you have your eye on a compact car or SUV. Choose the make and model you want, or alternatively enter the vehicle's price into the auto loan calculator. It will ask for a few other details such as the down payment, the loan term, the trade-in value and the interest rate. After that, it will calculate the compound interest, estimate tax and title fees, and display the monthly payment.`,
      `This car loan calculator will help you visualize how changes to your interest rate, down payment, trade-in value, and vehicle price affect your loan. Take some time to experiment with different values to find an auto loan setup that works best for your budget.`,
    ],
  },
  {
    title: 'Consider All Aspects of the Loan',
    items: [
      `The monthly payment is the best indicator of how the car loan will impact your budget. It can give you a reality check on whether you can afford the vehicle. And though this figure is the easiest to understand, it isn't the only number to be aware of.`,
      `It is also important to be aware of how much the loan will total, how much of a down payment you're making, and how long the loan will be. The general rule for each of these is as follows:`,
      `Your loan payment should be no more than 15% of your take-home pay. The loan term should ideally be less than 72 months, and you should aim for a down payment of at least 10% or consider GAP insurance.`,
      `Keep in mind that everyone's situation will be different, so these recommendations are not set in stone. Furthermore, these figures will differ for those who lease, so take a look at our articles for information specific to that scenario.`,
      `When you obtain a monthly payment, be it from a price quote, negotiation or advertised special, make sure you are aware of all the numbers behind it. What good is a low payment if it takes you 84 months to pay off the loan? Is the selling price for the car a good deal? What about the trade-in amount the dealership is offering for your car? Ask for the "out-the-door" figures from your salesperson and review them before making a decision.`,
    ],
  },
];

export const LOAN_FAQ = [
  {
    question: 'Can I get a loan with a credit score of 600?',
    answer: `Yes. The credit bureau Experian considers the range of 580-669 as "fair" or "subprime" borrowers, and about 17% of people fall into this credit score segment. It is possible to get a loan with a credit score of 600, but you'll likely have to make a larger down payment and have a higher interest rate than someone with a better credit score.`,
  },
  {
    question: 'How much of a car loan can I qualify for?',
    answer: `Everyone's situation will be different based on income and credit history. Edmunds recommends getting approved for a loan before going car shopping so that you know exactly how much of a car loan you qualify for.`,
  },
  {
    question: 'What is the minimum down payment for a car loan?',
    answer: `It is possible to take out a car loan with zero down, but know that it will make your monthly payment significantly higher and start you off with negative equity. Instead, we recommend a down payment of at least 10% and more if you can comfortably handle it.`,
  },
  {
    question: 'How much would a $30,000 car cost per month?',
    answer: `This all depends on the sales tax, the down payment, the interest rate and the length of the loan. But just as a ballpark estimate, assuming $3,000 down, an interest rate of 5.8% and a 60-month loan, the monthly payment would be about $520.`,
  },
  {
    question: 'What is a good interest rate on a car loan?',
    answer: `The lower the interest rate the better on a car loan. The average new-car interest rate in 2020 is close to 6%. Anything lower than that would be ideal. Similarly, the average used-car interest rate is about 9%, so you'd want to find a rate lower than that.`,
  },
];

export const CALCULATOR_LEASE = {
  SUBHEADER: 'Edmunds Lease Calculator will help you estimate your monthly car payment on a new car or truck lease.',
  TITLE: 'Calculate a lease payment estimate',
};
export const CALCULATOR_LOAN = {
  HEADER: 'Auto Loan Calculator - Monthly Car Loan Payment Calculator',
  SUBHEADER:
    'Calculate your monthly car payment estimate on a used car loan or a new car loan and find a great deal on a vehicle near you.',
  TITLE: 'Car payment calculator estimate',
};

export const AMOUNT_OWED_ON_TRADE_IN_LABEL = 'Amount Owed on Your Trade-in';
export const VALUE_OF_YOUR_TRADE_IN_LABEL = 'Value of Your Trade-in';
export const APPLIED_TRADE_IN_LABEL = 'Applied Trade-in';

export const TAXES_FEES_SUBTITLE_DEFAULT = 'Includes acquisition & dealer fee in amount shown';
export const TAXES_FEES_SUBTITLE = 'Acquisition & dealer fee added separately';

export const DEFAULT_VEHICLE = { year: 2021, id: 401864446, publicationState: INVENTORY_TYPES.USED };
